import React from 'react';
import { Calendar, CalendarChangeEvent } from 'primereact/calendar';
import moment, { Moment } from 'moment';

interface PrimeTimePickerProps {
  value?: Moment | null;
  defaultValue?: Moment | null;
  onChange?: (value: Moment | null) => void;
  disabled?: boolean;
  showSecond?: boolean;
  format?: string;
  use12Hours?: boolean;
  allowEmpty?: boolean;
  minuteStep?: number;
  focusOnOpen?: boolean;
  inputReadOnly?: boolean;
  className?: string;
}

/**
 * Wrapper component that adapts PrimeReact Calendar (in time mode)
 * to work as a drop-in replacement for rc-time-picker.
 * Handles conversion between Moment.js objects and Date objects.
 */
export const PrimeTimePicker: React.FC<PrimeTimePickerProps> = ({
  value,
  defaultValue,
  onChange,
  disabled = false,
  showSecond = false,
  format = 'HH:mm',
  use12Hours = false,
  allowEmpty = true,
  minuteStep = 1,
  focusOnOpen = false,
  inputReadOnly = false,
  className = '',
}) => {
  // Convert moment to Date for PrimeReact Calendar
  const momentToDate = (m: Moment | null | undefined): Date | null => {
    if (!m || !moment.isMoment(m)) return null;
    return m.toDate();
  };

  // Convert Date to moment for callbacks
  const dateToMoment = (d: Date | null | undefined): Moment | null => {
    if (!d) return null;
    return moment(d);
  };

  const dateValue = momentToDate(value ?? defaultValue);

  const handleChange = (e: CalendarChangeEvent) => {
    if (onChange) {
      // PrimeReact Calendar can return Date, Date[], string, or null/undefined
      let dateValue: Date | null | undefined;

      if (Array.isArray(e.value)) {
        dateValue = e.value[0];
      } else if (e.value instanceof Date) {
        dateValue = e.value;
      } else if (typeof e.value === 'string') {
        dateValue = new Date(e.value);
      } else {
        dateValue = null;
      }

      const momentValue = dateToMoment(dateValue);
      onChange(momentValue);
    }
  };

  // Determine time format based on props
  let timeFormat = format;
  if (use12Hours) {
    timeFormat = showSecond ? 'hh:mm:ss A' : 'hh:mm A';
  } else {
    timeFormat = showSecond ? 'HH:mm:ss' : 'HH:mm';
  }

  return (
    <Calendar
      value={dateValue}
      onChange={handleChange}
      timeOnly
      hourFormat={use12Hours ? '12' : '24'}
      showSeconds={showSecond}
      disabled={disabled}
      readOnlyInput={inputReadOnly}
      stepMinute={minuteStep}
      showTime
      className={className}
      inputStyle={{
        width: '80px',
        padding: '4px 8px',
        fontSize: '14px',
      }}
      panelStyle={{
        fontSize: '14px',
      }}
    />
  );
};

export default PrimeTimePicker;
